import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import clsx from "clsx";

import SectionHeader from "@/sections/common/SectionHeader";
import CTAButton from "@/components/atoms/buttons/CTAButton";

import { IPentester } from "@/components/cards/PentesterCard";
import { ITextButton } from "@/components/atoms/buttons/types";

// ----------------------------------------------------------------------------
// CertifiedPentestersSection — founders showcase on the home page.
//
// All content (heading, description, designations, photos) flows from
// Strapi via the blocks.certified-pentester-section block. The component
// keeps no hardcoded overrides — when the section reads wrong, update the
// CMS, not the code.
//
// Layout:
//   - LEFT  (col-span-5) : SectionHeader (eyebrow + title + subtitle) + CTA
//   - RIGHT (col-span-7) : 1 or 2 founder cards stacked or side-by-side
//
// Card chrome matches the legacy PentesterCard format (light primary-100
// panel, name + designation at top, natural-aspect photo in the middle,
// description at the bottom) so the headshots aren't masked into a shape
// that crops the body. Sizing + typography aligned to the canonical clamp
// pattern used by every other redesigned home section.
// ----------------------------------------------------------------------------

export type ICertifiedPentestersSectionContent = {
  heading?: string;
  description?: ReactNode;
  button?: ITextButton;
  pentesters?: IPentester[];
};

export type ICertifiedPentestersSectionProps = HTMLAttributes<HTMLElement> &
  ICertifiedPentestersSectionContent;

const CertifiedPentestersSection: FC<ICertifiedPentestersSectionProps> = (
  props,
) => {
  const { heading, description, button, pentesters, className, ...other } =
    props;

  if (!heading && !description && !button && !pentesters?.length) {
    return null;
  }

  return (
    <section
      className={clsx(
        "container mx-auto py-10 md:py-14 lg:py-16 xl:py-20",
        className,
      )}
      {...other}
    >
      <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14 xl:gap-20">
        {/* LEFT — header + CTA */}
        <div className="text-center lg:col-span-5 lg:text-left">
          <SectionHeader
            eyebrow="Our founders"
            title={heading}
            subtitle={description}
            align="left"
            className="!mx-0 !max-w-none lg:!text-left"
          />

          {button?.link && button?.text && (
            <div className="mt-6 flex justify-center md:mt-8 lg:justify-start">
              <CTAButton
                id={button.id}
                href={button.link}
                target={button.target}
                variant="orange"
                radius="full"
                size="md"
                showIcon
                iconType="arrow"
                iconPosition="right"
              >
                {button.text}
              </CTAButton>
            </div>
          )}
        </div>

        {/* RIGHT — founder cards */}
        {pentesters && pentesters.length > 0 && (
          <div
            className={clsx(
              "grid gap-5 lg:col-span-7 lg:gap-6",
              pentesters.length === 1
                ? "mx-auto max-w-sm grid-cols-1"
                : "grid-cols-1 sm:grid-cols-2",
            )}
          >
            {pentesters.map((p, idx) => (
              <FounderCard key={idx} pentester={p} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
// FounderCard — mirrors the legacy PentesterCard chrome (light card, name
// + designation on top, natural-aspect photo, description at bottom) but
// uses the canonical typography scale + responsive font weight so the
// section reads in the same family as the rest of the home page.
// ----------------------------------------------------------------------------

const FounderCard: FC<{ pentester: IPentester }> = ({ pentester }) => {
  const { name, designation, imageUrl, description } = pentester;

  return (
    <article className="flex h-full flex-col rounded-2xl bg-primary-100 px-6 py-7 text-center transition-transform duration-300 hover:-translate-y-1 md:px-8 md:py-8">
      {/* Name + designation block — canonical Bricolage Grotesque clamp
          scale, matching the section headings used elsewhere. */}
      <div className="mb-5">
        {name && (
          <h3 className="font-bricolage-grotesque text-[clamp(1.125rem,0.75vw+0.875rem,1.5rem)] font-medium !leading-tight text-black-950">
            {name}
          </h3>
        )}
        {designation && (
          <p className="mt-1.5 font-bricolage-grotesque text-[clamp(0.875rem,0.4vw+0.75rem,1.125rem)] font-normal text-primary-900">
            {designation}
          </p>
        )}
      </div>

      {/* Photo — natural aspect, capped at 160px so faces render at the
          same proportions the original PentesterCard used. No shape
          crop, no object-cover — the photo shows exactly as authored. */}
      {imageUrl?.src && (
        <div className="mx-auto mb-5 w-40 max-w-full">
          <Image
            src={imageUrl.src}
            alt={imageUrl.alt || name || "Founder"}
            width={160}
            height={186}
            className="h-auto w-full"
          />
        </div>
      )}

      {description && (
        <p className="break-words text-[clamp(0.875rem,0.3vw+0.8rem,1rem)] font-light leading-relaxed text-black-800 lg:font-extralight">
          {description}
        </p>
      )}
    </article>
  );
};

export default CertifiedPentestersSection;
